import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { useParams } from "react-router";
import { RefreshCw } from "lucide-react";

import { getMessagingEmail, refreshMessagingEmail } from "@/api/messaging";
import { EventBadge } from "@/components/messaging/badges";
import { ErrorBox, MutationError, Spinner } from "@/components/Spinner";
import { Button, Page, PageHeader } from "@/components/ui";
import { formatDateTime } from "@/lib/dates";
import { kindLabel } from "@/lib/messaging";

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </>
  );
}

export function MessagingEmailDetailPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const { emailId = "" } = useParams();
  const queryClient = useQueryClient();

  const email = useQuery({
    queryKey: ["messaging", "email", emailId],
    queryFn: () => getMessagingEmail(token, emailId),
    enabled: !!token && !!emailId,
  });

  const refresh = useMutation({
    mutationFn: () => refreshMessagingEmail(token, emailId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["messaging"] }),
  });

  return (
    <Page>
      <PageHeader
        back={{ to: "/messaging/emails", label: "Emails" }}
        // The subject names the email; while it loads the title is a bar, and if it cannot load the
        // page still says what it is.
        title={email.data ? email.data.subject || "(no subject)" : email.isError ? "Email" : undefined}
        status={email.data && <EventBadge event={email.data.lastEvent} />}
        // Only a waitlist email can be re-read from its provider; the other transport reports through events.
        actions={
          email.data?.transport === "resend" ? (
            <Button
              variant="secondary"
              icon={RefreshCw}
              loading={refresh.isPending}
              onClick={() => refresh.mutate()}
              title="Ask the delivery provider for the latest status of this email"
            >
              {refresh.isPending ? "Refreshing…" : "Refresh from provider"}
            </Button>
          ) : undefined
        }
      />

      {email.isLoading && <Spinner />}
      {email.isError && <ErrorBox error={email.error} />}
      <MutationError mutation={refresh} title="Could not refresh the email" />
      {email.data && (
        <>
          <div className="card mb-4">
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <DetailRow label="To" value={email.data.to.join(", ")} />
              <DetailRow label="From" value={email.data.from} />
              <DetailRow label="Sent" value={formatDateTime(email.data.sentAt)} />
              <DetailRow label="Last event" value={<EventBadge event={email.data.lastEvent} />} />
              <DetailRow label="Kind" value={kindLabel(email.data.kind)} />
              <DetailRow
                label="Tenant / stage"
                value={
                  email.data.tenantId
                    ? `${email.data.tenantId}${email.data.stage ? ` / ${email.data.stage}` : ""}`
                    : "—"
                }
              />
              <DetailRow label="Origin" value={email.data.origin === "emitter" ? "recorded on send" : "first seen via delivery event"} />
              <DetailRow label="Message id" value={<span className="font-mono text-xs">{email.data.messageId ?? "—"}</span>} />
            </dl>
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-medium text-ink">Delivery timeline</h2>
            <ol className="space-y-2 text-sm">
              {[...email.data.events]
                .sort((a, b) => a.at - b.at)
                .map((event, index) => (
                  <li key={`${event.type}-${event.at}-${index}`} className="flex items-start gap-3">
                    <span className="w-40 shrink-0 font-mono text-xs text-ink-muted">
                      {formatDateTime(event.at)}
                    </span>
                    <EventBadge event={event.type} />
                    {event.detail && (
                      <span className="text-ink-muted">
                        {Object.entries(event.detail)
                          .filter(([, v]) => v !== null && v !== undefined && v !== "")
                          .map(([k, v]) => `${k}: ${String(v)}`)
                          .join(" · ")}
                      </span>
                    )}
                  </li>
                ))}
            </ol>
          </div>
        </>
      )}
    </Page>
  );
}
